//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import BusinessTypeModel from '../models/business-type.model';

/**
 * @service BusinessTypeService
 * @description Service for managing business type definitions.
 * Provides HTTP methods for retrieving business types from the backend API.
 * Used to categorize business entities (e.g., Farm, Provider, Hatchery).
 * Extends base Service class with generic type BusinessTypeModel.
 *
 * @feature 1. Fetch all business types
 * @feature 2. Filter business types by business category
 * @feature 3. Cancel ongoing HTTP requests
 *
 * @architecture
 * Extends base Service<BusinessTypeModel>. Provides standard CRUD access
 * to business type reference data.
 *
 * @service-endpoint /tradingcommunity/businesstype/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessTypeService extends Service<BusinessTypeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/businesstype/');
  }
  
  /**
   * @method getAllBusinessTypes
   * @async
   * @description Fetch all business type records from the backend
   *
   * Makes HTTP GET request to retrieve all business types with pagination.
   *
   * @returns {Promise<Array<BusinessTypeModel>>} Promise resolving to array of BusinessTypeModel objects
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllBusinessTypes() {
    return new Promise<Array<BusinessTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getBusinessTypesByBusinessCategory
   * @async
   * @description Fetch business types filtered by a specific business category ID
   *
   * @param {string} businessId - Business Category ID to filter by
   * @returns {Promise<Array<BusinessTypeModel>>} Promise resolving to filtered array of BusinessTypeModel objects
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getBusinessTypesByBusinessCategory(businessId: string) {
    return new Promise<Array<BusinessTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessTypeModel>>(`${this.baseUrl}${this.serviceUrl}?businessCategoryID=${businessId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
